import animationTable from "../art/animations.spec.json";
import artSpec from "../art/art-spec.json";
import type { ArtAsset } from "../art/validate";
import type { LooksView, Mood } from "../ui/view-model";
import { drawBackground, drawCompanion, drawDecoration, drawToy } from "./items";

export const SCENE_WIDTH = 80;
export const SCENE_HEIGHT = 60;

export interface Canvas2D {
  fillStyle: string | CanvasGradient | CanvasPattern;
  globalAlpha?: number;
  fillRect(x: number, y: number, w: number, h: number): void;
  clearRect?(x: number, y: number, w: number, h: number): void;
}

function pickColour(ramp: readonly string[], index: number): string {
  const colour = ramp[index];
  if (!colour) {
    throw new Error(`Missing master palette colour at index ${index}`);
  }
  return colour;
}

const PALETTE = {
  waterBands: [
    pickColour(artSpec.masterPalette.blue, 2),
    pickColour(artSpec.masterPalette.teal, 2),
    pickColour(artSpec.masterPalette.blue, 1),
    pickColour(artSpec.masterPalette.teal, 1),
    pickColour(artSpec.masterPalette.teal, 1),
    pickColour(artSpec.masterPalette.teal, 0),
  ],
  ray: pickColour(artSpec.masterPalette.blue, 3),
  bubbleLight: pickColour(artSpec.masterPalette.blue, 4),
  sandLight: pickColour(artSpec.masterPalette.sand, 2),
  sandDark: pickColour(artSpec.masterPalette.sand, 1),
  weed: [
    pickColour(artSpec.masterPalette.green, 0),
    pickColour(artSpec.masterPalette.green, 1),
    pickColour(artSpec.masterPalette.green, 2),
  ],
} as const;

/** Which frames play for which mood, per asset class: the one table the art and the code share. */
const ANIMATIONS: Record<string, Record<Mood, readonly string[]>> = animationTable;

/**
 * Deterministic LCG matching `rng(s)` in docs/handoff/006-shell-reference.html.
 */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function px(ctx: Canvas2D, x: number, y: number, w: number, h: number, colour: string, scale = 1): void {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  if (ix < 0 || iy < 0 || ix >= w || iy >= h) return;
  ctx.fillStyle = colour;
  ctx.fillRect(ix * scale, iy * scale, scale, scale);
}

/**
 * Flat colour bands with a checkerboard dither on every seam (docs/handoff/006-shell-reference.html).
 */
function drawDitheredBands(
  ctx: Canvas2D,
  y0: number,
  y1: number,
  w: number,
  h: number,
  cols: readonly string[],
  scale = 1,
): void {
  const n = cols.length;
  const hh = (y1 - y0) / n;
  for (let y = y0; y < y1; y++) {
    const i = Math.min(n - 1, Math.floor((y - y0) / hh));
    ctx.fillStyle = cols[i]!;
    ctx.fillRect(0, y * scale, w * scale, scale);
  }
  for (let i = 1; i < n; i++) {
    const sy = Math.round(y0 + i * hh);
    for (let x = 0; x < w; x++) {
      if ((x + sy) % 2 === 0) {
        px(ctx, x, sy, w, h, cols[i]!, scale);
      } else {
        px(ctx, x, sy - 1, w, h, cols[i - 1]!, scale);
      }
      if ((x + sy) % 2 === 1 && sy + 1 < y1) {
        px(ctx, x, sy + 1, w, h, cols[i]!, scale);
      }
    }
  }
}

/**
 * Maps an asset class ("pet" or "swimmer"), mood, and animation tick to a valid required frame name.
 */
export function moodToFrame(assetClass: string, mood: Mood, tick = 0): string {
  const t = Math.abs(Math.trunc(tick));
  const frames = (ANIMATIONS[assetClass] ?? ANIMATIONS.pet!)[mood];
  return frames[t % frames.length]!;
}

/**
 * Draws a single frame of an ArtAsset onto a 2D canvas context with top-left at (x, y) and pixel size `scale`.
 */
export function drawFrame(
  ctx: Canvas2D,
  asset: ArtAsset,
  frameName: string,
  x: number,
  y: number,
  scale = 1,
): void {
  const rows = asset.frames[frameName];
  if (!rows) return;
  const pixelScale = Math.max(1, Math.round(scale));

  for (let py = 0; py < rows.length; py++) {
    const row = rows[py];
    if (!row) continue;
    for (let pxX = 0; pxX < row.length; pxX++) {
      const ch = row[pxX];
      if (!ch || ch === artSpec.transparent) continue;
      const colour = asset.palette[ch];
      if (!colour) continue;
      ctx.fillStyle = colour;
      ctx.fillRect(x + pxX * pixelScale, y + py * pixelScale, pixelScale, pixelScale);
    }
  }
}

export interface TankSceneOptions {
  tick?: number;
  reducedMotion?: boolean;
}

const WEED_POSITIONS = [
  [6, 44],
  [11, 45],
  [70, 44],
  [74, 45],
  [64, 46],
] as const;

/**
 * Draws the underwater tank background on the 80x60 pixel grid (or scaled logical grid)
 * matching docs/handoff/006-shell-reference.html: dithered water bands, pixelated light rays,
 * curved sandy floor with pebbles, swaying seaweed, and rising bubbles.
 */
export function drawTankScene(
  ctx: Canvas2D,
  width = SCENE_WIDTH,
  height = SCENE_HEIGHT,
  options: TankSceneOptions = {},
): void {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const tick = options.reducedMotion ? 0 : Math.abs(Math.trunc(options.tick ?? 0));
  const reducedMotion = options.reducedMotion ?? true;

  ctx.clearRect?.(0, 0, w, h);

  const sandY = Math.round((44 / SCENE_HEIGHT) * h);
  drawDitheredBands(ctx, 0, sandY, w, h, PALETTE.waterBands, 1);

  // Pixelated diagonal light rays from the surface
  const rayMaxY = Math.round((26 / SCENE_HEIGHT) * h);
  for (let i = 0; i < 4; i++) {
    const baseRx = Math.round(((8 + i * 20) / SCENE_WIDTH) * w);
    const sway = reducedMotion ? 0 : Math.round(Math.sin(tick * 0.25 + i) * 3);
    const rx = baseRx + sway;
    for (let y = 0; y < rayMaxY; y++) {
      const rw = 1 + Math.floor(y / 9);
      for (let x = 0; x < rw; x++) {
        if ((x + y) % 2 === 0) {
          px(ctx, rx + Math.floor(y / 3) + x, y, w, h, PALETTE.ray);
        }
      }
    }
  }

  // Curved sandy floor and deterministic pebbles
  for (let x = 0; x < w; x++) {
    const sh = Math.round(sandY - Math.sin(x / 8) * 1.5);
    for (let y = sh; y < h; y++) {
      px(ctx, x, y, w, h, y < sh + 3 ? PALETTE.sandLight : PALETTE.sandDark);
    }
  }
  const r = rng(11);
  const pebbleTop = Math.min(h - 2, sandY + 4);
  const pebbleSpan = Math.max(1, h - pebbleTop - 2);
  for (let i = 0; i < 26; i++) {
    const x = Math.floor(r() * Math.max(1, w - 2)) + 1;
    const y = pebbleTop + Math.floor(r() * pebbleSpan);
    px(ctx, x, y, w, h, PALETTE.sandDark);
    px(ctx, x + 1, y, w, h, PALETTE.sandDark);
  }

  // Swaying seaweed stalks on the same pixel grid
  WEED_POSITIONS.forEach(([bx, by], j) => {
    const scaledBx = Math.round((bx / SCENE_WIDTH) * w);
    const scaledBy = Math.round((by / SCENE_HEIGHT) * h);
    for (let k = 0; k < 10; k++) {
      const sw = reducedMotion ? 0 : Math.round(Math.sin(tick * 0.65 + j + k / 3) * 1.4);
      px(ctx, scaledBx + sw, scaledBy - k, w, h, PALETTE.weed[(k + j) % 3]!);
      if (k % 3 === 1) {
        px(ctx, scaledBx + sw + 1, scaledBy - k, w, h, PALETTE.weed[1]!);
      }
    }
  });

  // Rising pixel bubbles when motion is enabled
  if (!reducedMotion) {
    for (let i = 0; i < 5; i++) {
      const span = Math.max(1, sandY);
      const by = Math.round(sandY - (((tick * 4 + i * 17) % span + span) % span));
      const baseBx = Math.round(((14 + i * 14) / SCENE_WIDTH) * w);
      const bx = baseBx + Math.round(Math.sin(tick * 0.9 + i) * 2);
      px(ctx, bx, by, w, h, PALETTE.bubbleLight);
      if (i % 2 === 1) {
        px(ctx, bx + 1, by, w, h, PALETTE.ray);
      }
    }
  }
}

export interface RenderPetSceneOptions {
  width?: number;
  height?: number;
  asset: ArtAsset;
  mood: Mood;
  tick: number;
  reducedMotion: boolean;
  scale?: number;
  looks?: LooksView;
  sleeping?: boolean;
}

const BOB_OFFSETS = [0, -1, -2, -1, 0, 1, 2, 1] as const;

const Z_SPOTS = [
  [42, 24],
  [50, 18],
  [56, 10],
] as const;

const Z_PIXELS = [
  [0, 0], [1, 0], [2, 0], [3, 0], [4, 0],
  [3, 1],
  [2, 2],
  [1, 3],
  [0, 4], [1, 4], [2, 4], [3, 4], [4, 4],
] as const;

/**
 * Draws the 80x60 tank background scene and the 40x40 pet sprite at 1:1 on the same pixel grid,
 * plus decoration, toy, and companion if configured in `looks`.
 * Draw order: drawBG, drawDecor, pet, drawToy, drawComp, sleeping overlay (reference order).
 */
export function renderPetScene(ctx: Canvas2D, options: RenderPetSceneOptions): void {
  const width = Math.max(1, Math.round(options.width ?? SCENE_WIDTH));
  const height = Math.max(1, Math.round(options.height ?? SCENE_HEIGHT));
  const { asset, mood, tick, reducedMotion, looks, sleeping } = options;
  const scale = Math.max(1, Math.round(options.scale ?? 1));

  const effectiveTick = reducedMotion ? 0 : Math.abs(Math.trunc(tick));
  drawBackground(ctx, looks?.background ?? 0, effectiveTick, reducedMotion);
  drawDecoration(ctx, looks?.decoration ?? -1);

  const candidateFrame = moodToFrame(asset.class, sleeping ? "resting" : mood, effectiveTick);
  const frameName =
    candidateFrame in asset.frames ? candidateFrame : (Object.keys(asset.frames)[0] ?? candidateFrame);

  const canBob = !reducedMotion && mood !== "dead" && mood !== "lastChance" && !sleeping;
  const bobStep = canBob ? BOB_OFFSETS[effectiveTick % BOB_OFFSETS.length]! * scale : 0;

  const anchorTargetX = Math.round(width / 2);
  const anchorTargetY =
    asset.class === "swimmer"
      ? Math.round((28 / SCENE_HEIGHT) * height) + bobStep
      : Math.round((48 / SCENE_HEIGHT) * height) + bobStep;

  const originX = anchorTargetX - asset.anchor.x * scale;
  const originY = anchorTargetY - asset.anchor.y * scale;

  drawFrame(ctx, asset, frameName, originX, originY, scale);

  drawToy(ctx, looks?.toy ?? -1);
  drawCompanion(ctx, looks?.companion ?? -1, effectiveTick, reducedMotion);

  if (sleeping) {
    const prevAlpha = ctx.globalAlpha;
    if (ctx.globalAlpha !== undefined) {
      ctx.globalAlpha = 0.35;
    }
    ctx.fillStyle = pickColour(artSpec.masterPalette.ink, 3);
    ctx.fillRect(0, 0, width, height);
    if (ctx.globalAlpha !== undefined && prevAlpha !== undefined) {
      ctx.globalAlpha = prevAlpha;
    }

    const zColour = pickColour(artSpec.masterPalette.gold, 4);
    Z_SPOTS.forEach(([baseX, baseY], zi) => {
      const drift = reducedMotion ? 0 : (effectiveTick * 2 + zi * 4) % 14;
      const sway = reducedMotion ? 0 : Math.round(Math.sin(effectiveTick * 0.8 + zi) * 2);
      const zx = (baseX + sway) * scale;
      const zy = (baseY - drift) * scale;
      ctx.fillStyle = zColour;
      for (const [pxX, pxY] of Z_PIXELS) {
        ctx.fillRect(zx + pxX * scale, zy + pxY * scale, scale, scale);
      }
    });
  }
}
